import { Helmet } from 'react-helmet-async';
import { Divider, Loader, Paper } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import useReportStore from './Report.store';
import dayjs from '@/libs/dayjs';
// mantine
// components
import PageContainer from '@/components/Layout/components/PageContainer';
import Header from '@/components/Header';
import Summary from '@/pages/Report/components/Summary';
import ASR from '@/pages/Report/components/ASR';
import ReportMeta from '@/pages/Report/components/ReportMeta';
import TotalSessions from '@/pages/Report/components/TotalSessions';
import SessionDistribution from '@/pages/Report/components/SessionDistribution';
// utils
import { ReportProvider } from '@/pages/Report/Report.context';
import { useSessionsInfoQuery } from '@/services/bff/bff.queries.ts';

const MAX_DATE_RANGE_IN_MONTHS = 3;

const Report = () => {
    const { dateRange, setDateRange, getDateRangeValue } = useReportStore();
    const { data } = useSessionsInfoQuery(getDateRangeValue());

    return (
        <>
            <Helmet>
                <title>NEWO - AI Employee Performance Report</title>
            </Helmet>
            <PageContainer className="pb-20">
                <Header title="Report" />
                {data ? (
                    <ReportProvider value={getDateRangeValue()}>
                        <div className="flex flex-col gap-10 order-2 xl:order-1">
                            <div className="flex flex-col-reverse lg:flex-row justify-between gap-4">
                                <ReportMeta />
                                <DatePickerInput
                                    allowSingleDateInRange
                                    highlightToday
                                    className="min-w-[20rem]"
                                    excludeDate={(date) => {
                                        const threeMonthsAgo = dayjs().subtract(
                                            MAX_DATE_RANGE_IN_MONTHS,
                                            'month'
                                        );
                                        return (
                                            dayjs(date).isAfter(dayjs()) ||
                                            dayjs(date).isBefore(threeMonthsAgo)
                                        );
                                    }}
                                    label="Select Period"
                                    maxLevel="year"
                                    type="range"
                                    value={dateRange}
                                    onChange={setDateRange}
                                />
                            </div>

                            <Summary data={data.summary} />
                            <ASR
                                data={data.sessions_by_asr}
                                totalASR={
                                    data.summary.asr.working_hours +
                                    data.summary.asr.non_working_hours
                                }
                            />
                            <Paper
                                className="flex flex-col gap-4 p-4 lg:p-6 border-solid border border-gray-3"
                                radius="md"
                            >
                                <TotalSessions
                                    data={data.sessions_by_leads}
                                    deals={data.summary.additional_deal.total}
                                />
                                <Divider />
                                <SessionDistribution
                                    byChannel={data.sessions_by_channel}
                                    byType={data.sessions_by_type}
                                    summary={data.summary}
                                />
                            </Paper>
                        </div>
                    </ReportProvider>
                ) : (
                    <div className="flex items-center justify-center py-60">
                        <Loader />
                    </div>
                )}
            </PageContainer>
        </>
    );
};

export default Report;
